import { useCallback, useEffect, useState, type RefObject } from "react"

/**
 * Wraps the browser Fullscreen API for a specific element.
 *
 * - `isFullscreen` mirrors `document.fullscreenElement === ref.current`, so
 *   pressing Esc (the browser-driven exit) keeps React state in sync.
 * - `enter()` / `exit()` are guarded — calling them in the wrong state is a
 *   no-op, never a thrown error.
 * - Includes WebKit prefix fallbacks for older Safari.
 *
 * Designed for the video / document share layouts: the caller attaches the
 * ref to the *content* element they want fullscreened (not the meeting
 * shell) so the participant rail / control bar are excluded.
 */

interface VendorDocument extends Document {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void>
}

interface VendorElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>
}

const getActiveFullscreenElement = (): Element | null => {
  const doc = document as VendorDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

const requestFs = async (el: HTMLElement): Promise<void> => {
  const vendor = el as VendorElement
  if (typeof el.requestFullscreen === "function") {
    return el.requestFullscreen()
  }
  if (typeof vendor.webkitRequestFullscreen === "function") {
    return vendor.webkitRequestFullscreen()
  }
  return Promise.reject(new Error("Fullscreen API not supported"))
}

const exitFs = async (): Promise<void> => {
  const doc = document as VendorDocument
  if (typeof document.exitFullscreen === "function") {
    return document.exitFullscreen()
  }
  if (typeof doc.webkitExitFullscreen === "function") {
    return doc.webkitExitFullscreen()
  }
}

export interface UseFullscreenResult {
  isFullscreen: boolean
  enter: () => void
  exit: () => void
  toggle: () => void
}

export function useFullscreen(
  ref: RefObject<HTMLElement | null>,
): UseFullscreenResult {
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Re-resolve on every fullscreenchange — covers user-driven Esc, F11,
  // and programmatic transitions in other parts of the app.
  useEffect(() => {
    const sync = () => {
      const target = ref.current
      setIsFullscreen(target !== null && getActiveFullscreenElement() === target)
    }
    sync()
    document.addEventListener("fullscreenchange", sync)
    document.addEventListener("webkitfullscreenchange", sync)
    return () => {
      document.removeEventListener("fullscreenchange", sync)
      document.removeEventListener("webkitfullscreenchange", sync)
    }
  }, [ref])

  // Defensive: if the host unmounts while still owning the fullscreen layer
  // (e.g. the consumer's parent flipped `presentingVideo` back to null),
  // make sure we drop out of fullscreen so the meeting chrome doesn't stay
  // hidden. Browsers exit on DOM removal automatically, but doing this
  // explicitly is cheap and removes a race.
  useEffect(() => {
    return () => {
      if (getActiveFullscreenElement() === ref.current) {
        void exitFs().catch(() => {})
      }
    }
  }, [ref])

  const enter = useCallback(() => {
    const el = ref.current
    if (!el || getActiveFullscreenElement() === el) return
    requestFs(el)
      .then(() => {
        // Put keyboard focus on the thing that just went fullscreen.
        //
        // Whatever was focused before usually still is — and if that was an
        // embedded player or document, its iframe keeps every keystroke. The
        // shortcuts listen on `window`, so nothing in this document hears them
        // and the meeting appears to have stopped responding to the keyboard
        // altogether. Browsers do not move focus on their own here.
        const target = ref.current
        if (!target) return
        const active = document.activeElement
        if (active instanceof HTMLIFrameElement) active.blur()
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
        target.focus({ preventScroll: true })
      })
      .catch((err) => {
        console.warn("[useFullscreen] enter failed", err)
      })
  }, [ref])

  const exit = useCallback(() => {
    if (!getActiveFullscreenElement()) return
    exitFs().catch((err) => {
      console.warn("[useFullscreen] exit failed", err)
    })
  }, [])

  const toggle = useCallback(() => {
    if (isFullscreen) exit()
    else enter()
  }, [isFullscreen, enter, exit])

  return { isFullscreen, enter, exit, toggle }
}
